import { useState, useEffect } from "react";
import CalculatorsPage from "./pages/CalculatorsPage";
import BlogPage from "./pages/BlogPage";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
const GF = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');`;

const FEATURES = [
  {
    icon: "◉",
    title: "Account Aggregation",
    desc: "Connect all your bank accounts, credit cards, and wallets in one place. See your complete financial picture instantly.",
  },
  {
    icon: "⊞",
    title: "Smart Transactions",
    desc: "Every transaction auto-categorised and searchable. Filter by merchant, category, or date range with zero effort.",
  },
  {
    icon: "◎",
    title: "Budget Tracking",
    desc: "Set monthly budgets per category, watch your spending in real time, and get alerts before you overspend.",
  },
  {
    icon: "△",
    title: "Investment Portfolio",
    desc: "Track stocks, ETFs, and crypto alongside your cash. Watch net worth grow with live balance snapshots.",
  },
  {
    icon: "◈",
    title: "Net Worth Dashboard",
    desc: "A single glance shows total assets, liabilities, and a 12-month projection of where you're headed.",
  },
  {
    icon: "↓",
    title: "Export Reports",
    desc: "Download CSV or printable HTML reports for any section — taxes, reviews, or just your own records.",
  },
];

const STEPS = [
  { n: "01", title: "Create your account", desc: "Sign up free in under a minute. No credit card required to start." },
  { n: "02", title: "Connect your accounts", desc: "Link banks, cards, and investments securely via Plaid." },
  { n: "03", title: "Take control", desc: "Ledger does the categorising. You focus on the decisions that matter." },
];

// ─── Login / Sign-up Modal ────────────────────────────────────────────────────
function AuthModal({ defaultTab, onClose, onLogin }) {
  const [tab,              setTab]              = useState(defaultTab || "login");
  const [loading,          setLoading]          = useState(false);
  const [name,             setName]             = useState("");
  const [email,            setEmail]            = useState("");
  const [password,         setPassword]         = useState("");
  const [error,            setError]            = useState("");
  const [success,          setSuccess]          = useState("");
  const [pendingVerify,    setPendingVerify]     = useState(false);  // show check-email screen
  const [registeredEmail,  setRegisteredEmail]  = useState("");
  const [unverified,       setUnverified]       = useState(false);   // login blocked — not verified yet
  const [resendLoading,    setResendLoading]     = useState(false);
  const [resendMsg,        setResendMsg]         = useState("");
  // 2FA challenge
  const [showMfa,   setShowMfa]   = useState(false);
  const [mfaToken,  setMfaToken]  = useState("");
  const [mfaCode,   setMfaCode]   = useState("");
  // Forgot password
  const [showForgot,       setShowForgot]       = useState(false);
  const [forgotEmail,      setForgotEmail]      = useState("");
  const [forgotSent,       setForgotSent]       = useState(false);
  const [forgotLoading,    setForgotLoading]    = useState(false);

  const inp = {
    width: "100%", padding: "12px 14px",
    background: "var(--bg-main)", border: "1px solid var(--border-strong)",
    borderRadius: "10px", color: "var(--text-primary)", fontSize: "14px",
    fontFamily: "'Capsule Sans', sans-serif", outline: "none", boxSizing: "border-box",
  };
  const lbl = {
    display: "block", fontSize: "11px", color: "var(--text-muted)",
    letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px",
    fontFamily: "'JetBrains Mono', monospace",
  };

  const handleRegister = async () => {
    if (!name || !email || !password) { setError("Please fill all fields."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      const r = await fetch(`${API}/api/v1/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      const d = await r.json();
      if (r.ok) {
        setRegisteredEmail(email);
        setPendingVerify(true);
      } else {
        setError(d.errors?.[0]?.msg || d.message || d.error || "Registration failed.");
      }
    } catch (e) { setError("Connection error. Please try again."); }
    finally { setLoading(false); }
  };

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter email and password."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      const r = await fetch(`${API}/api/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (r.ok && d.mfaRequired) {
        // User has 2FA enabled — show authenticator code screen
        setMfaToken(d.mfaToken);
        setShowMfa(true);
        setError("");
      } else if (r.ok && d.accessToken) {
        // Token stored in React state only — never localStorage
        setSuccess("Welcome back!");
        setTimeout(() => onLogin(d.accessToken), 600);
      } else if (r.status === 403 && d.code === "EMAIL_NOT_VERIFIED") {
        setUnverified(true);
        setError("");
      } else {
        setError(d.errors?.[0]?.msg || d.message || d.error || "Invalid credentials.");
      }
    } catch (e) { setError("Connection error. Please try again."); }
    finally { setLoading(false); }
  };

  const handleMfaVerify = async () => {
    if (!mfaCode.trim()) { setError("Please enter your 6-digit code."); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/v1/auth/totp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfaToken, code: mfaCode.trim() }),
      });
      const d = await r.json();
      if (r.ok && d.accessToken) {
        setSuccess("Welcome back!");
        setTimeout(() => onLogin(d.accessToken), 600);
      } else {
        setError(d.errors?.[0]?.msg || d.error || "Invalid code. Please try again.");
      }
    } catch { setError("Connection error. Please try again."); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    const targetEmail = registeredEmail || email;
    if (!targetEmail) return;
    setResendLoading(true); setResendMsg("");
    try {
      await fetch(`${API}/api/v1/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: targetEmail }),
      });
      setResendMsg("✅ A new verification link has been sent.");
    } catch { setResendMsg("❌ Could not resend — please try again."); }
    finally { setResendLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000); // 15s max
    try {
      await fetch(`${API}/api/v1/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
        signal: controller.signal,
      });
    } catch { /* swallow — network error or timeout */ }
    finally {
      clearTimeout(timer);
      // Always show "check your email" — prevents enumeration and unsticks the UI
      setForgotSent(true);
      setForgotLoading(false);
    }
  };

  // ── 2FA challenge screen ─────────────────────────────────────────────────
  if (showMfa) {
    return (
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: "380px", background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "20px", padding: "36px", boxShadow: "0 32px 80px rgba(0,0,0,0.7)", textAlign: "center" }}>
          <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", margin: "0 auto 20px" }}>◈</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "8px" }}>Two-Factor Auth</div>
          <div style={{ color: "var(--text-muted)", fontSize: "13px", marginBottom: "24px" }}>Enter the 6-digit code from your authenticator app, or a backup code.</div>
          {error && <div style={{ background: "#2d1a1a", border: "1px solid #5a2a2a", borderRadius: "8px", padding: "10px 14px", color: "#e07070", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
          <input
            type="text" inputMode="numeric" pattern="[0-9]*" maxLength={8}
            placeholder="000 000"
            value={mfaCode}
            onChange={e => { setMfaCode(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleMfaVerify()}
            autoFocus
            style={{ width: "100%", padding: "14px", textAlign: "center", letterSpacing: "0.3em", fontSize: "22px", fontFamily: "'JetBrains Mono', monospace", background: "var(--bg-main)", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-primary)", outline: "none", marginBottom: "16px", boxSizing: "border-box" }}
          />
          <button onClick={handleMfaVerify} disabled={loading} style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", border: "none", borderRadius: "10px", color: "#0D1410", fontWeight: "700", fontSize: "15px", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, marginBottom: "12px" }}>
            {loading ? "Verifying…" : "Verify →"}
          </button>
          <button onClick={() => { setShowMfa(false); setMfaCode(""); setMfaToken(""); setError(""); }} style={{ background: "none", border: "none", color: "var(--text-faint)", fontSize: "13px", cursor: "pointer" }}>
            ← Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── Forgot password screen ────────────────────────────────────────────────
  if (showForgot) {
    return (
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: "400px", background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "20px", padding: "36px", boxShadow: "0 32px 80px rgba(0,0,0,0.7)", textAlign: "center" }}>
          <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", margin: "0 auto 20px" }}>◈</div>
          {forgotSent ? (
            <>
              <div style={{ fontSize: "28px", marginBottom: "10px" }}>📬</div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "8px" }}>Check your email</div>
              <div style={{ color: "var(--text-muted)", fontSize: "13px", marginBottom: "28px", lineHeight: "1.6" }}>
                If an account with <strong style={{ color: "#D4AF37" }}>{forgotEmail}</strong> exists, we've sent a password reset link. Check your inbox and spam folder.
              </div>
              <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }} style={{ background: "none", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-muted)", fontSize: "13px", cursor: "pointer", padding: "10px 20px" }}>
                ← Back to sign in
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "8px" }}>Reset your password</div>
              <div style={{ color: "var(--text-muted)", fontSize: "13px", marginBottom: "24px" }}>Enter your email and we'll send you a reset link.</div>
              <div style={{ textAlign: "left", marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px", fontFamily: "'JetBrains Mono', monospace" }}>Email</label>
                <input
                  type="email" placeholder="alex@example.com"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleForgotPassword()}
                  autoFocus
                  style={{ width: "100%", padding: "12px 14px", background: "var(--bg-main)", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-primary)", fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", outline: "none", boxSizing: "border-box" }}
                />
              </div>
              <button onClick={handleForgotPassword} disabled={forgotLoading || !forgotEmail} style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", border: "none", borderRadius: "10px", color: "#0D1410", fontWeight: "700", fontSize: "15px", cursor: (forgotLoading || !forgotEmail) ? "not-allowed" : "pointer", opacity: (forgotLoading || !forgotEmail) ? 0.7 : 1, marginBottom: "12px" }}>
                {forgotLoading ? "Sending…" : "Send Reset Link →"}
              </button>
              <button onClick={() => { setShowForgot(false); setForgotEmail(""); }} style={{ background: "none", border: "none", color: "var(--text-faint)", fontSize: "13px", cursor: "pointer" }}>
                ← Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Check-your-email screen (shown right after registration) ──────────────
  if (pendingVerify) {
    return (
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: "420px", background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "20px", padding: "36px", boxShadow: "0 32px 80px rgba(0,0,0,0.7)", textAlign: "center" }}>
          <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", margin: "0 auto 16px" }}>◈</div>
          <div style={{ fontSize: "28px", marginBottom: "10px" }}>📬</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "12px" }}>Check your email</div>
          <div style={{ color: "var(--text-muted)", fontSize: "14px", lineHeight: "1.6", marginBottom: "24px" }}>
            We sent a verification link to<br />
            <span style={{ color: "#D4AF37", fontWeight: "600" }}>{registeredEmail}</span>.<br /><br />
            Click the link in the email to activate your account.
          </div>
          {resendMsg && <div style={{ color: resendMsg.startsWith("✅") ? "#5fad6b" : "#e05c5c", fontSize: "13px", marginBottom: "16px" }}>{resendMsg}</div>}
          <button onClick={handleResend} disabled={resendLoading} style={{ background: "none", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-muted)", fontSize: "13px", padding: "10px 18px", cursor: "pointer", marginBottom: "12px", width: "100%" }}>
            {resendLoading ? "Sending…" : "Resend verification email"}
          </button>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-faint)", fontSize: "13px", cursor: "pointer" }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: "420px",
          background: "var(--bg-card)", border: "1px solid var(--border-strong)",
          borderRadius: "20px", padding: "32px",
          boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
          animation: "modalIn 0.22s ease",
        }}
      >
        <style>{`
          @keyframes modalIn { from { opacity:0; transform: translateY(16px) scale(0.97); } to { opacity:1; transform: none; } }
        `}</style>

        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: "16px", right: "16px",
            background: "none", border: "none", color: "var(--text-muted)",
            fontSize: "18px", cursor: "pointer", lineHeight: 1,
          }}
        >×</button>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <div style={{
            width: "44px", height: "44px", borderRadius: "12px",
            background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", margin: "0 auto 10px",
          }}>◈</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: "var(--text-primary)", fontFamily: "'Capsule Sans', sans-serif" }}>Ledger</div>
          <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginTop: "2px" }}>
            {tab === "login" ? "SIGN IN TO YOUR ACCOUNT" : "CREATE YOUR FREE ACCOUNT"}
          </div>
        </div>

        {/* Unverified email banner */}
        {unverified && (
          <div style={{ background: "#1e150a", border: "1px solid #5a3a10", borderRadius: "10px", padding: "14px 16px", marginBottom: "16px", fontSize: "13px", color: "#e8a060", lineHeight: "1.5" }}>
            <strong>Email not verified.</strong> Please check your inbox for the verification link.
            <br />
            <span
              onClick={handleResend}
              style={{ color: "#D4AF37", cursor: "pointer", textDecoration: "underline", marginTop: "6px", display: "inline-block" }}
            >
              {resendLoading ? "Sending…" : resendMsg || "Resend verification email"}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: "flex", background: "var(--bg-main)", borderRadius: "10px",
          padding: "3px", marginBottom: "24px", border: "1px solid #222",
        }}>
          {["login", "signup"].map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(""); setSuccess(""); }}
              style={{
                flex: 1, padding: "9px", borderRadius: "8px", border: "none",
                background: tab === t ? "#1C2C1C" : "transparent",
                color: tab === t ? "#D4AF37" : "#777",
                fontWeight: tab === t ? "600" : "400",
                fontSize: "13px", cursor: "pointer",
                fontFamily: "'Capsule Sans', sans-serif",
                transition: "all 0.15s",
              }}
            >
              {t === "login" ? "Log In" : "Sign Up"}
            </button>
          ))}
        </div>

        {/* Fields */}
        {tab === "signup" && (
          <div style={{ marginBottom: "16px" }}>
            <label style={lbl}>Full Name</label>
            <input style={inp} type="text" placeholder="Alex Chen" value={name} onChange={e => setName(e.target.value)} />
          </div>
        )}
        <div style={{ marginBottom: "16px" }}>
          <label style={lbl}>Email</label>
          <input style={inp} type="email" placeholder="alex@example.com" value={email} onChange={e => setEmail(e.target.value)} />
        </div>
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Password</label>
            {tab === "login" && (
              <span
                onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotSent(false); }}
                style={{ fontSize: "11px", color: "#D4AF37", cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif", letterSpacing: "-0.01em" }}
              >
                Forgot password?
              </span>
            )}
          </div>
          <input
            style={inp} type="password"
            placeholder={tab === "signup" ? "Min 8 characters" : "Your password"}
            value={password} onChange={e => setPassword(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") tab === "login" ? handleLogin() : handleRegister(); }}
          />
        </div>

        {/* Feedback */}
        {error   && <div style={{ background: "#2a1010", border: "1px solid #5a2020", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#e07070", marginBottom: "16px" }}>{error}</div>}
        {success && <div style={{ background: "#0e2a18", border: "1px solid #1a5a30", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#52D68A", marginBottom: "16px" }}>{success}</div>}

        {/* CTA */}
        <button
          disabled={loading}
          onClick={tab === "login" ? handleLogin : handleRegister}
          style={{
            width: "100%", padding: "13px",
            background: loading ? "#333" : "linear-gradient(135deg, #D4AF37, #9A7C1E)",
            border: "none", borderRadius: "10px",
            color: loading ? "#666" : "#121A12",
            fontSize: "14px", fontWeight: "700", cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "'Capsule Sans', sans-serif", letterSpacing: "-0.01em",
            opacity: loading ? 0.6 : 1, transition: "opacity 0.2s",
          }}
        >
          {loading ? "Please wait…" : tab === "login" ? "Log In →" : "Start Free Trial →"}
        </button>

        {tab === "signup" && (
          <p style={{ textAlign: "center", fontSize: "11px", color: "var(--text-faint)", marginTop: "14px", fontFamily: "'Capsule Sans', sans-serif" }}>
            14-day free trial · No credit card required
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Landing Page ─────────────────────────────────────────────────────────────
export default function LandingPage({ onLogin }) {
  const [showAuth,   setShowAuth]   = useState(false);
  const [authTab,    setAuthTab]    = useState("login");
  const [scrolled,   setScrolled]   = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [view,       setView]       = useState("home"); // "home" | "calculators"
  const [menuOpen,   setMenuOpen]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setActiveStep(s => (s + 1) % STEPS.length), 2800);
    return () => clearInterval(t);
  }, []);

  const openLogin  = () => { setAuthTab("login");  setShowAuth(true); };
  const openSignup = () => { setAuthTab("signup"); setShowAuth(true); };

  return (
    <div style={{ minHeight: "100vh", background: "var(--sidebar-bg)", color: "var(--text-primary)", fontFamily: "'Capsule Sans', sans-serif" }}>
      <style>{`
        ${GF}
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        @keyframes fadeUp   { from { opacity:0; transform: translateY(32px); } to { opacity:1; transform: none; } }
        @keyframes shimmer  { 0%,100% { opacity:0.4; } 50% { opacity:0.9; } }
        @keyframes floatUp  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .hero-word { display:inline-block; animation: fadeUp 0.7s ease both; }
        .feature-card:hover { border-color: #243424 !important; transform: translateY(-3px); background: #1e1c16 !important; }
        .feature-card { transition: all 0.22s ease; }
        .cta-primary:hover { background: linear-gradient(135deg, #ddd0b8, #a08060) !important; transform: translateY(-1px); }
        .cta-primary { transition: all 0.18s ease; }
        .cta-ghost:hover  { border-color: #D4AF37 !important; color: #D4AF37 !important; }
        .cta-ghost { transition: all 0.18s ease; }
        .nav-login:hover { color: #F5F2EE !important; }
        .nav-login { transition: color 0.15s; }
        .nav-hamburger { display: none; }
        .nav-links { display: flex; align-items: center; gap: 32px; }
        .steps-line { position: absolute; top: 28px; left: calc(16.7% + 8px); right: calc(16.7% + 8px); height: 1px; background: linear-gradient(90deg, #1E2C1E, #D4AF3740, #1E2C1E); }

        @media (max-width: 768px) {
          .nav-hamburger {
            display: flex !important;
            align-items: center; justify-content: center;
            background: none; border: none; cursor: pointer;
            color: #F5F2EE; font-size: 22px; padding: 4px;
          }
          .nav-links {
            display: none !important;
          }
          .nav-links.open {
            display: flex !important;
            flex-direction: column;
            align-items: flex-start;
            position: absolute;
            top: 68px; left: 0; right: 0;
            background: rgba(13,12,10,0.98);
            backdrop-filter: blur(20px);
            padding: 20px 24px 28px;
            gap: 20px !important;
            border-bottom: 1px solid #1E2C1E;
            z-index: 499;
          }
          .nav-links.open button,
          .nav-links.open a {
            font-size: 15px !important;
          }
          .features-grid {
            grid-template-columns: 1fr !important;
          }
          .pricing-grid {
            grid-template-columns: 1fr !important;
          }
          .steps-row {
            flex-direction: column !important;
            align-items: center !important;
            gap: 36px !important;
          }
          .steps-line {
            display: none !important;
          }
          .footer-row {
            flex-direction: column !important;
            align-items: center !important;
            gap: 16px !important;
            text-align: center;
          }
          .dashboard-preview {
            display: none !important;
          }
          .hero-section {
            padding: 100px 24px 60px !important;
          }
          .section-hpad {
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
        }
      `}</style>

      {/* ── Navbar ──────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 500,
        padding: "0 40px",
        background: scrolled || menuOpen ? "rgba(13,12,10,0.96)" : "transparent",
        backdropFilter: scrolled || menuOpen ? "blur(20px)" : "none",
        borderBottom: scrolled || menuOpen ? "1px solid var(--border)" : "1px solid transparent",
        transition: "all 0.3s ease",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: "68px",
      }}>
        {/* Logo */}
        <div
          onClick={() => { setView("home"); setMenuOpen(false); }}
          style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
        >
          <div style={{
            width: "32px", height: "32px", borderRadius: "9px",
            background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "15px",
          }}>◈</div>
          <span style={{ fontSize: "17px", fontWeight: "800", letterSpacing: "-0.02em", color: "var(--text-primary)" }}>Ledger</span>
        </div>

        {/* Nav links (desktop) + mobile dropdown */}
        <div className={`nav-links${menuOpen ? " open" : ""}`}>
          {view === "home" ? (
            <>
              <a href="#features" onClick={() => setMenuOpen(false)} style={{ fontSize: "13px", color: "var(--text-muted)", textDecoration: "none" }} className="nav-login">Features</a>
              <a href="#how"      onClick={() => setMenuOpen(false)} style={{ fontSize: "13px", color: "var(--text-muted)", textDecoration: "none" }} className="nav-login">How it works</a>
              <a href="#pricing"  onClick={() => setMenuOpen(false)} style={{ fontSize: "13px", color: "var(--text-muted)", textDecoration: "none" }} className="nav-login">Pricing</a>
            </>
          ) : (
            <button onClick={() => { setView("home"); setMenuOpen(false); }} className="nav-login" style={{ background: "none", border: "none", fontSize: "13px", color: "var(--text-muted)", cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif" }}>← Home</button>
          )}

          <button
            onClick={() => { setView("calculators"); setMenuOpen(false); }}
            className="nav-login"
            style={{
              background: "none", border: "none", fontSize: "13px", cursor: "pointer",
              fontFamily: "'Capsule Sans', sans-serif", fontWeight: view === "calculators" ? "600" : "400",
              color: view === "calculators" ? "#D4AF37" : "#aaa",
              borderBottom: view === "calculators" ? "1px solid #D4AF37" : "1px solid transparent",
              paddingBottom: "2px",
            }}
          >∑ Calculators</button>

          <button
            onClick={() => { setView("blog"); setMenuOpen(false); }}
            className="nav-login"
            style={{
              background: "none", border: "none", fontSize: "13px", cursor: "pointer",
              fontFamily: "'Capsule Sans', sans-serif", fontWeight: view === "blog" ? "600" : "400",
              color: view === "blog" ? "#D4AF37" : "#aaa",
              borderBottom: view === "blog" ? "1px solid #D4AF37" : "1px solid transparent",
              paddingBottom: "2px",
            }}
          >✦ Blog</button>

          <div style={{ width: "1px", height: "20px", background: "var(--bg-hover)" }} />

          <button
            onClick={() => { openLogin(); setMenuOpen(false); }}
            className="nav-login"
            style={{
              background: "none", border: "none", fontSize: "13px",
              color: "var(--text-secondary)", cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
              fontWeight: "500",
            }}
          >Log In</button>

          <button
            onClick={() => { openSignup(); setMenuOpen(false); }}
            className="cta-primary"
            style={{
              padding: "9px 20px",
              background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
              border: "none", borderRadius: "20px",
              color: "#121A12", fontSize: "13px", fontWeight: "700",
              cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
            }}
          >Start Free Trial</button>
        </div>

        {/* Hamburger (mobile only) */}
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      </nav>

      {/* ── Calculators view ────────────────────────────────────────────────── */}
      {view === "calculators" && (
        <div style={{ paddingTop: "68px", minHeight: "100vh", background: "var(--sidebar-bg)" }}>
          <CalculatorsPage />
        </div>
      )}

      {/* ── Blog view ───────────────────────────────────────────────────────── */}
      {view === "blog" && (
        <div style={{ paddingTop: "68px", minHeight: "100vh", background: "var(--sidebar-bg)" }}>
          <BlogPage />
        </div>
      )}

      {/* ── Marketing content (home view only) ──────────────────────────────── */}
      {view === "home" && <>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="hero-section" style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "120px 40px 80px", textAlign: "center",
        background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(212,175,55,0.08) 0%, transparent 70%)",
      }}>
        {/* Badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          padding: "6px 16px", borderRadius: "20px",
          background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)",
          fontSize: "12px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace",
          marginBottom: "40px", letterSpacing: "0.05em",
          animation: "fadeUp 0.5s 0.1s ease both",
        }}>
          ✦ 14-DAY FREE TRIAL · NO CARD REQUIRED
        </div>

        {/* Headline */}
        <h1 style={{
          fontSize: "clamp(48px, 8vw, 88px)",
          fontWeight: "900", lineHeight: "1.0",
          letterSpacing: "-0.04em",
          marginBottom: "28px",
          maxWidth: "820px",
        }}>
          <span className="hero-word" style={{ animationDelay: "0.2s", color: "var(--text-primary)" }}>Your money,</span>{" "}
          <br />
          <span className="hero-word" style={{ animationDelay: "0.35s", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>finally clear.</span>
        </h1>

        {/* Subheadline */}
        <p style={{
          fontSize: "18px", color: "var(--text-muted)", maxWidth: "520px", lineHeight: "1.6",
          marginBottom: "44px", fontWeight: "400",
          animation: "fadeUp 0.7s 0.5s ease both",
        }}>
          Ledger connects all your accounts, tracks every transaction, and shows you exactly where your money goes — all in one beautiful dashboard.
        </p>

        {/* CTAs */}
        <div style={{
          display: "flex", gap: "14px", flexWrap: "wrap", justifyContent: "center",
          animation: "fadeUp 0.7s 0.65s ease both",
        }}>
          <button
            onClick={openSignup}
            className="cta-primary"
            style={{
              padding: "15px 32px",
              background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
              border: "none", borderRadius: "12px",
              color: "#121A12", fontSize: "15px", fontWeight: "700",
              cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
              letterSpacing: "-0.01em",
            }}
          >
            Start Free Trial →
          </button>
          <button
            onClick={openLogin}
            className="cta-ghost"
            style={{
              padding: "15px 32px",
              background: "transparent", border: "1px solid var(--border-strong)",
              borderRadius: "12px", color: "var(--text-secondary)",
              fontSize: "15px", fontWeight: "500",
              cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
            }}
          >
            Log In
          </button>
        </div>

        {/* Social proof */}
        <p style={{
          fontSize: "12px", color: "var(--text-faint)", marginTop: "32px",
          fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.06em",
          animation: "fadeUp 0.7s 0.8s ease both",
        }}>
          ACCOUNTS · TRANSACTIONS · BUDGETS · INVESTMENTS · REPORTS
        </p>
      </section>

      {/* ── Dashboard Preview ────────────────────────────────────────────────── */}
      <section className="dashboard-preview section-hpad" style={{ padding: "0 40px 100px", display: "flex", justifyContent: "center" }}>
        <div style={{
          width: "100%", maxWidth: "960px",
          background: "var(--bg-main)", border: "1px solid var(--border)",
          borderRadius: "20px", overflow: "hidden",
          boxShadow: "0 40px 100px rgba(0,0,0,0.6)",
        }}>
          {/* Fake browser bar */}
          <div style={{
            background: "var(--sidebar-bg)", padding: "12px 16px",
            display: "flex", alignItems: "center", gap: "8px",
            borderBottom: "1px solid #121C12",
          }}>
            {["#e07070","#e0c870","#70d080"].map(c => (
              <div key={c} style={{ width: "11px", height: "11px", borderRadius: "50%", background: c, opacity: 0.7 }} />
            ))}
            <div style={{
              flex: 1, margin: "0 16px", background: "var(--bg-card)", borderRadius: "6px",
              padding: "5px 12px", fontSize: "11px", color: "var(--text-faint)",
              fontFamily: "'JetBrains Mono', monospace",
            }}>ledger.app/dashboard</div>
          </div>

          {/* Dashboard mockup */}
          <div style={{ display: "flex", height: "340px" }}>
            {/* Sidebar */}
            <div style={{ width: "180px", background: "var(--bg-deepest)", padding: "20px 12px", borderRight: "1px solid #121C12", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "24px", paddingLeft: "4px" }}>
                <div style={{ width: "22px", height: "22px", borderRadius: "7px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" }}>◈</div>
                <span style={{ fontSize: "13px", fontWeight: "800", color: "var(--text-primary)" }}>Ledger</span>
              </div>
              {[
                { label: "Overview", active: true },
                { label: "Accounts",    active: false },
                { label: "Transactions",active: false },
                { label: "Budget",      active: false },
                { label: "Investments", active: false },
              ].map(({ label, active }) => (
                <div key={label} style={{
                  padding: "8px 10px", borderRadius: "7px", marginBottom: "2px",
                  background: active ? "rgba(212,175,55,0.12)" : "transparent",
                  color: active ? "#D4AF37" : "#666",
                  fontSize: "12px", fontWeight: active ? "600" : "400",
                  borderLeft: active ? "2px solid #D4AF37" : "2px solid transparent",
                }}>{label}</div>
              ))}
            </div>

            {/* Main area */}
            <div style={{ flex: 1, padding: "20px 24px", overflow: "hidden" }}>
              {/* Stats row */}
              <div style={{ display: "flex", gap: "14px", marginBottom: "20px" }}>
                {[
                  { label: "Net Worth",   value: "$142,840", change: "+4.2%" },
                  { label: "Total Assets",value: "$168,200", change: "+2.1%" },
                  { label: "Monthly Save",value: "$3,200",   change: "+$400" },
                ].map(({ label, value, change }) => (
                  <div key={label} style={{
                    flex: 1, background: "var(--bg-card)", borderRadius: "12px",
                    padding: "14px 16px", border: "1px solid var(--border-strong)",
                  }}>
                    <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "6px" }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--text-primary)", letterSpacing: "-0.03em" }}>{value}</div>
                    <div style={{ fontSize: "11px", color: "#52D68A", marginTop: "3px" }}>{change}</div>
                  </div>
                ))}
              </div>

              {/* Chart placeholder */}
              <div style={{
                background: "var(--bg-card)", borderRadius: "12px", padding: "16px",
                border: "1px solid var(--border)", height: "160px",
                display: "flex", alignItems: "flex-end", gap: "6px",
              }}>
                {[40, 55, 42, 70, 58, 85, 72, 90, 68, 95, 88, 100].map((h, i) => (
                  <div key={i} style={{
                    flex: 1, borderRadius: "4px 4px 0 0",
                    background: i === 11 ? "linear-gradient(180deg, #D4AF37, #9A7C1E)" : "rgba(212,175,55,0.15)",
                    height: `${h}%`,
                    animation: `shimmer ${1.5 + i * 0.1}s infinite ease`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────────── */}
      <section id="features" className="section-hpad" style={{ padding: "80px 40px", maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "64px" }}>
          <div style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", marginBottom: "16px" }}>EVERYTHING YOU NEED</div>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 52px)", fontWeight: "800", letterSpacing: "-0.03em", color: "var(--text-primary)", lineHeight: "1.1" }}>
            One dashboard for<br />
            <span style={{ background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>your entire financial life</span>
          </h2>
        </div>

        <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
          {FEATURES.map(({ icon, title, desc }) => (
            <div
              key={title}
              className="feature-card"
              style={{
                background: "var(--bg-main)", border: "1px solid var(--border)",
                borderRadius: "16px", padding: "28px",
              }}
            >
              <div style={{
                width: "44px", height: "44px", borderRadius: "12px",
                background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "20px", color: "#D4AF37", marginBottom: "18px",
              }}>{icon}</div>
              <h3 style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "10px", letterSpacing: "-0.02em" }}>{title}</h3>
              <p style={{ fontSize: "14px", color: "var(--text-muted)", lineHeight: "1.6" }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────────── */}
      <section id="how" style={{ padding: "80px 40px", background: "#0a0908" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", marginBottom: "16px" }}>SIMPLE BY DESIGN</div>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 52px)", fontWeight: "800", letterSpacing: "-0.03em", color: "var(--text-primary)", marginBottom: "60px" }}>
            Up and running in minutes
          </h2>

          <div className="steps-row" style={{ display: "flex", gap: "0", position: "relative" }}>
            {/* Connecting line */}
            <div className="steps-line" style={{
              position: "absolute", top: "28px", left: "calc(16.7% + 8px)", right: "calc(16.7% + 8px)",
              height: "1px", background: "linear-gradient(90deg, #1E2C1E, #D4AF3740, #1E2C1E)",
            }} />

            {STEPS.map(({ n, title, desc }, i) => (
              <div
                key={n}
                style={{
                  flex: 1, padding: "0 24px", textAlign: "center",
                  transition: "all 0.3s ease",
                }}
              >
                <div style={{
                  width: "56px", height: "56px", borderRadius: "50%", margin: "0 auto 20px",
                  background: activeStep === i
                    ? "linear-gradient(135deg, #D4AF37, #9A7C1E)"
                    : "#121C12",
                  border: activeStep === i ? "none" : "1px solid var(--border-strong)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "13px", fontWeight: "700", color: activeStep === i ? "#121A12" : "#555",
                  fontFamily: "'JetBrains Mono', monospace",
                  transition: "all 0.3s ease",
                  position: "relative", zIndex: 1,
                }}>{n}</div>
                <h3 style={{ fontSize: "16px", fontWeight: "700", color: activeStep === i ? "#F5F2EE" : "#aaa", marginBottom: "10px", transition: "color 0.3s" }}>{title}</h3>
                <p style={{ fontSize: "13px", color: "var(--text-faint)", lineHeight: "1.6" }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────────────── */}
      <section id="pricing" className="section-hpad" style={{ padding: "80px 40px", maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "56px" }}>
          <div style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", marginBottom: "16px" }}>PRICING</div>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 52px)", fontWeight: "800", letterSpacing: "-0.03em", color: "var(--text-primary)" }}>
            Simple, transparent pricing
          </h2>
        </div>

        <div className="pricing-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
          {/* Free Trial */}
          <div style={{
            background: "var(--bg-main)", border: "1px solid var(--border-strong)",
            borderRadius: "20px", padding: "36px",
          }}>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "16px" }}>FREE TRIAL</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "8px" }}>
              <span style={{ fontSize: "48px", fontWeight: "900", color: "var(--text-primary)", letterSpacing: "-0.04em" }}>$0</span>
              <span style={{ fontSize: "14px", color: "var(--text-muted)" }}> / 14 days</span>
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "28px", lineHeight: "1.5" }}>Try everything free. No credit card needed.</p>
            <div style={{ height: "1px", background: "#182218", marginBottom: "24px" }} />
            {["All dashboard features", "Unlimited account linking", "Transaction history", "Budget + investment tracking", "CSV & PDF exports"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <span style={{ color: "#52D68A", fontSize: "14px" }}>✓</span>
                <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>{f}</span>
              </div>
            ))}
            <button
              onClick={openSignup}
              className="cta-ghost"
              style={{
                marginTop: "28px", width: "100%", padding: "13px",
                background: "transparent", border: "1px solid var(--border-strong)",
                borderRadius: "10px", color: "var(--text-secondary)",
                fontSize: "14px", fontWeight: "600",
                cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
              }}
            >Start Free Trial</button>
          </div>

          {/* Premium */}
          <div style={{
            background: "linear-gradient(145deg, #1e1a12, #16130d)",
            border: "1px solid rgba(212,175,55,0.3)",
            borderRadius: "20px", padding: "36px", position: "relative", overflow: "hidden",
          }}>
            {/* Glow */}
            <div style={{
              position: "absolute", top: "-40px", right: "-40px",
              width: "150px", height: "150px", borderRadius: "50%",
              background: "radial-gradient(circle, rgba(212,175,55,0.15), transparent 70%)",
            }} />
            <div style={{ fontSize: "12px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "16px" }}>PREMIUM</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "8px" }}>
              <span style={{ fontSize: "48px", fontWeight: "900", color: "var(--text-primary)", letterSpacing: "-0.04em" }}>$9</span>
              <span style={{ fontSize: "20px", fontWeight: "700", color: "#D4AF37" }}>.99</span>
              <span style={{ fontSize: "14px", color: "var(--text-muted)" }}> / month</span>
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "28px", lineHeight: "1.5" }}>Everything in the trial, forever.</p>
            <div style={{ height: "1px", background: "rgba(212,175,55,0.15)", marginBottom: "24px" }} />
            {["Everything in Free Trial", "Priority support", "Advanced analytics & projections", "Recurring bills tracker", "AI-powered spending insights", "Unlimited data history"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <span style={{ color: "#D4AF37", fontSize: "14px" }}>✦</span>
                <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{f}</span>
              </div>
            ))}
            <button
              onClick={openSignup}
              className="cta-primary"
              style={{
                marginTop: "28px", width: "100%", padding: "13px",
                background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
                border: "none", borderRadius: "10px",
                color: "#121A12", fontSize: "14px", fontWeight: "700",
                cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
              }}
            >Get Premium →</button>
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────────── */}
      <section style={{
        padding: "100px 40px", textAlign: "center",
        background: "radial-gradient(ellipse 70% 50% at 50% 100%, rgba(212,175,55,0.06) 0%, transparent 70%)",
      }}>
        <h2 style={{ fontSize: "clamp(36px, 6vw, 64px)", fontWeight: "900", letterSpacing: "-0.04em", color: "var(--text-primary)", marginBottom: "20px" }}>
          Take control of<br />
          <span style={{ background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>your finances today.</span>
        </h2>
        <p style={{ fontSize: "16px", color: "var(--text-muted)", maxWidth: "440px", margin: "0 auto 40px", lineHeight: "1.6" }}>
          Join thousands who finally understand where their money goes every month.
        </p>
        <button
          onClick={openSignup}
          className="cta-primary"
          style={{
            padding: "16px 40px",
            background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
            border: "none", borderRadius: "14px",
            color: "#121A12", fontSize: "16px", fontWeight: "700",
            cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif", letterSpacing: "-0.01em",
          }}
        >
          Start Free — No Card Needed →
        </button>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer className="footer-row section-hpad" style={{
        padding: "32px 40px", borderTop: "1px solid #121C12",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "22px", height: "22px", borderRadius: "7px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" }}>◈</div>
          <span style={{ fontSize: "14px", fontWeight: "800", color: "var(--text-primary)", letterSpacing: "-0.01em" }}>Ledger</span>
        </div>
        <p style={{ fontSize: "12px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
          © {new Date().getFullYear()} Ledger · Personal Finance
        </p>
        <button
          onClick={openLogin}
          style={{
            background: "none", border: "none", fontSize: "13px",
            color: "var(--text-muted)", cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
          }}
          className="nav-login"
        >Subscriber login →</button>
      </footer>

      </>} {/* end home view */}

      {/* ── Auth Modal (available on both views) ────────────────────────────── */}
      {showAuth && (
        <AuthModal
          defaultTab={authTab}
          onClose={() => setShowAuth(false)}
          onLogin={onLogin}
        />
      )}
    </div>
  );
}
